 
import type { RouteRecord } from 'vite-react-ssg'

import App from '@/App'
import NotFoundPage from './not-found'

import { GuestOnlyRoute } from '@/components/auth/route-guards'
import { recoverFromChunkError } from '@/lib/chunk-reload'
import { getPubliclyPublishedBlogPaths } from '@/data/blog'
import { getComparisonPaths } from '@/data/comparisons'

// Map a module whose default export is a React component to React Router's
// lazy shape ({ Component }). Lets page files keep their natural default exports.
function page(loader: () => Promise<{ default: React.ComponentType<unknown> }>) {
  return async () => {
    try {
      const mod = await loader()
      return { Component: mod.default }
    } catch (error) {
      // The route's code-split chunk failed to load — usually a stale asset
      // after a deploy or a transient network drop. Reload once to fetch the
      // current build; if we've already reloaded, rethrow so the failure
      // surfaces instead of looping.
      recoverFromChunkError()
      throw error
    }
  }
}

export const routes: RouteRecord[] = [
  {
    path: '/',
    element: <App />,
    entry: 'src/App.tsx',
    children: [
      // Marketing
      {
        lazy: page(() => import('@/layouts/marketing-layout')),
        entry: 'src/layouts/marketing-layout.tsx',
        children: [
          { index: true, lazy: page(() => import('./marketing/home')), entry: 'src/routes/marketing/home.tsx' },
          { path: 'about', lazy: page(() => import('./marketing/about')), entry: 'src/routes/marketing/about.tsx' },
          { path: 'features', lazy: page(() => import('./marketing/features')), entry: 'src/routes/marketing/features.tsx' },
          { path: 'pricing', lazy: page(() => import('./marketing/pricing')), entry: 'src/routes/marketing/pricing.tsx' },
          { path: 'solutions', lazy: page(() => import('./marketing/solutions')), entry: 'src/routes/marketing/solutions.tsx' },
          { path: 'agencies', lazy: page(() => import('./marketing/agencies')), entry: 'src/routes/marketing/agencies.tsx' },
          { path: 'resources', lazy: page(() => import('./marketing/resources')), entry: 'src/routes/marketing/resources.tsx' },
          { path: 'integrations', lazy: page(() => import('./marketing/integrations')), entry: 'src/routes/marketing/integrations.tsx' },
          { path: 'wordpress-plugin', lazy: page(() => import('./marketing/wordpress-plugin')), entry: 'src/routes/marketing/wordpress-plugin.tsx' },
          // Marketing page for Live Giving. The token pages under /live/… are
          // separate routes further down and never prerendered.
          { path: 'live', lazy: page(() => import('./marketing/live')), entry: 'src/routes/marketing/live.tsx' },
          { path: 'tools', lazy: page(() => import('./marketing/tools')), entry: 'src/routes/marketing/tools.tsx' },
          { path: 'tools/pledge-counter', lazy: page(() => import('./marketing/pledge-counter')), entry: 'src/routes/marketing/pledge-counter.tsx' },
          { path: 'tools/zakat-calculator', lazy: page(() => import('./marketing/zakat-calculator')), entry: 'src/routes/marketing/zakat-calculator.tsx' },
          { path: 'tools/donation-fee-calculator', lazy: page(() => import('./marketing/donation-fee-calculator')), entry: 'src/routes/marketing/donation-fee-calculator.tsx' },
          { path: 'tools/gift-range-chart', lazy: page(() => import('./marketing/gift-range-chart')), entry: 'src/routes/marketing/gift-range-chart.tsx' },
          { path: 'tools/tithe-calculator', lazy: page(() => import('./marketing/tithe-calculator')), entry: 'src/routes/marketing/tithe-calculator.tsx' },
          { path: 'tools/donation-receipt-generator', lazy: page(() => import('./marketing/donation-receipt-generator')), entry: 'src/routes/marketing/donation-receipt-generator.tsx' },
          { path: 'tools/recurring-giving-calculator', lazy: page(() => import('./marketing/recurring-giving-calculator')), entry: 'src/routes/marketing/recurring-giving-calculator.tsx' },
          { path: 'tools/sponsorship-tracker', lazy: page(() => import('./marketing/sponsorship-tracker')), entry: 'src/routes/marketing/sponsorship-tracker.tsx' },
          { path: 'tools/qr-poster', lazy: page(() => import('./marketing/qr-poster')), entry: 'src/routes/marketing/qr-poster.tsx' },
          { path: 'tools/year-end-statements', lazy: page(() => import('./marketing/year-end-statements')), entry: 'src/routes/marketing/year-end-statements.tsx' },
          { path: 'privacy', lazy: page(() => import('./marketing/privacy')), entry: 'src/routes/marketing/privacy.tsx' },
          { path: 'terms', lazy: page(() => import('./marketing/terms')), entry: 'src/routes/marketing/terms.tsx' },
          { path: 'editorial-standards', lazy: page(() => import('./marketing/editorial-standards')), entry: 'src/routes/marketing/editorial-standards.tsx' },
          { path: 'contact-sales', lazy: page(() => import('./marketing/contact-sales')), entry: 'src/routes/marketing/contact-sales.tsx' },
          { path: 'blog', lazy: page(() => import('./marketing/blog')), entry: 'src/routes/marketing/blog.tsx' },
          { path: 'compare', lazy: page(() => import('./marketing/compare')), entry: 'src/routes/marketing/compare.tsx' },
          {
            path: 'compare/:slug',
            lazy: page(() => import('./marketing/comparison')),
            entry: 'src/routes/marketing/comparison.tsx',
            getStaticPaths: getComparisonPaths,
          },
          {
            path: 'blog/:slug',
            lazy: page(() => import('./marketing/blog-post')),
            entry: 'src/routes/marketing/blog-post.tsx',
            getStaticPaths: getPubliclyPublishedBlogPaths,
          },
        ],
      },
      { path: 'client-setup/:organizationId', lazy: page(() => import('./auth/client-setup')), entry: 'src/routes/auth/client-setup.tsx' },
      { path: 'client-handoff/:organizationId', lazy: page(() => import('./auth/client-handoff')), entry: 'src/routes/auth/client-handoff.tsx' },
      // Auth
      {
        lazy: page(() => import('@/layouts/auth-layout')),
        entry: 'src/layouts/auth-layout.tsx',
        children: [
          // Guest-only entry points: signed-in viewers are routed to the app
          // instead of ever seeing the login / signup / forgot-password forms.
          {
            element: <GuestOnlyRoute />,
            children: [
              { path: 'login', lazy: page(() => import('./auth/login')), entry: 'src/routes/auth/login.tsx' },
              { path: 'signup', lazy: page(() => import('./auth/signup')), entry: 'src/routes/auth/signup.tsx' },
              { path: 'forgot-password', lazy: page(() => import('./auth/forgot-password')), entry: 'src/routes/auth/forgot-password.tsx' },
            ],
          },
          // Token-driven flows stay reachable regardless of session state (the
          // viewer may be signed out, mid-signup, or signed in on another tab).
          { path: 'reset-password', lazy: page(() => import('./auth/reset-password')), entry: 'src/routes/auth/reset-password.tsx' },
          { path: 'verify-email', lazy: page(() => import('./auth/verify-email')), entry: 'src/routes/auth/verify-email.tsx' },
          { path: 'two-factor', lazy: page(() => import('./auth/two-factor')), entry: 'src/routes/auth/two-factor.tsx' },
          { path: 'invite', lazy: page(() => import('./auth/invite')), entry: 'src/routes/auth/invite.tsx' },
          { path: 'join-organization', lazy: page(() => import('./auth/join-organization')), entry: 'src/routes/auth/join-organization.tsx' },
        ],
      },
      {
        path: 'soogal', lazy: page(() => import('./soogal/layout')), entry: 'src/routes/soogal/layout.tsx',
        children: [
          { index: true, lazy: page(() => import('./soogal/overview')), entry: 'src/routes/soogal/overview.tsx' },
          { path: 'organizations', lazy: page(() => import('./soogal/organizations')), entry: 'src/routes/soogal/organizations.tsx' },
          { path: 'reports', lazy: page(() => import('./soogal/reports')), entry: 'src/routes/soogal/reports.tsx' },
          { path: 'acquisition', lazy: page(() => import('./soogal/acquisition')), entry: 'src/routes/soogal/acquisition.tsx' },
          { path: 'targets', lazy: page(() => import('./soogal/targets')), entry: 'src/routes/soogal/targets.tsx' },
          { path: 'audit', lazy: page(() => import('./soogal/audit')), entry: 'src/routes/soogal/audit.tsx' },
        ],
      },
      // Dashboard
      {
        lazy: page(() => import('@/layouts/app-layout')),
        entry: 'src/layouts/app-layout.tsx',
        children: [
          { path: 'new-client', lazy: page(() => import('./app/new-client')), entry: 'src/routes/app/new-client.tsx' },
          { path: 'dashboard', lazy: page(() => import('./app/dashboard')), entry: 'src/routes/app/dashboard.tsx' },
          { path: 'forms', lazy: page(() => import('./app/forms')), entry: 'src/routes/app/forms.tsx' },
          { path: 'forms/:formId', lazy: page(() => import('./app/form')), entry: 'src/routes/app/form.tsx' },
          { path: 'campaigns', lazy: page(() => import('./app/campaigns')), entry: 'src/routes/app/campaigns.tsx' },
          { path: 'campaigns/:campaignId', lazy: page(() => import('./app/campaign')), entry: 'src/routes/app/campaign.tsx' },
          { path: 'floating-button', lazy: page(() => import('./app/floating-button')), entry: 'src/routes/app/floating-button.tsx' },
          { path: 'live-giving', lazy: page(() => import('./app/live-giving')), entry: 'src/routes/app/live-giving.tsx' },
          { path: 'live-giving/new', lazy: page(() => import('./app/live-giving-new')), entry: 'src/routes/app/live-giving-new.tsx' },
          { path: 'live-giving/:eventId', lazy: page(() => import('./app/live-event')), entry: 'src/routes/app/live-event.tsx' },
          { path: 'live-giving/:eventId/edit', lazy: page(() => import('./app/live-giving-edit')), entry: 'src/routes/app/live-giving-edit.tsx' },
          { path: 'live-giving/:eventId/report', lazy: page(() => import('./app/live-report')), entry: 'src/routes/app/live-report.tsx' },
          // Legacy single-form route — keeps existing links/bookmarks working by
          // opening the org's primary form in the editor.
          { path: 'form', lazy: page(() => import('./app/form')), entry: 'src/routes/app/form.tsx' },
          { path: 'donors', lazy: page(() => import('./app/donors')), entry: 'src/routes/app/donors.tsx' },
          { path: 'donors/:id', lazy: page(() => import('./app/donor-detail')), entry: 'src/routes/app/donor-detail.tsx' },
          { path: 'donations', lazy: page(() => import('./app/donations')), entry: 'src/routes/app/donations.tsx' },
          { path: 'donations/:id', lazy: page(() => import('./app/donation-detail')), entry: 'src/routes/app/donation-detail.tsx' },
          { path: 'search', lazy: page(() => import('./app/search')), entry: 'src/routes/app/search.tsx' },
          { path: 'settings', lazy: page(() => import('./app/settings')), entry: 'src/routes/app/settings.tsx' },
          { path: 'stripe-app', lazy: page(() => import('./app/stripe-app')), entry: 'src/routes/app/stripe-app.tsx' },
          { path: 'stripe-app/mapping', lazy: page(() => import('./app/stripe-app-mapping')), entry: 'src/routes/app/stripe-app-mapping.tsx' },
          { path: 'stripe-app/setup', lazy: page(() => import('./app/stripe-app-setup')), entry: 'src/routes/app/stripe-app-setup.tsx' },
          { path: 'docs', lazy: page(() => import('./app/docs')), entry: 'src/routes/app/docs.tsx' },
          {
            path: 'docs/:section/:slug',
            lazy: page(() => import('./app/docs-article')),
            entry: 'src/routes/app/docs-article.tsx',
          },
        ],
      },
      // Onboarding
      {
        lazy: page(() => import('@/layouts/onboarding-layout')),
        entry: 'src/layouts/onboarding-layout.tsx',
        children: [
          { path: 'onboarding', lazy: page(() => import('./onboarding/onboarding')), entry: 'src/routes/onboarding/onboarding.tsx' },
        ],
      },
      // Give (public donation flow)
      {
        path: 'give',
        lazy: page(() => import('@/layouts/give-layout')),
        entry: 'src/layouts/give-layout.tsx',
        children: [
          {
            path: ':organizationSlug/campaigns/:campaignSlug',
            lazy: page(() => import('./give/campaign')),
            entry: 'src/routes/give/campaign.tsx',
          },
          {
            path: ':organizationSlug/campaigns/:campaignSlug/widgets/:widget',
            lazy: page(() => import('./give/campaign-widget')),
            entry: 'src/routes/give/campaign-widget.tsx',
          },
          {
            // Embeddable campaign card (goal + progress + Donate), framed inline.
            path: ':organizationSlug/campaigns/:campaignSlug/card',
            lazy: page(() => import('./give/campaign-card')),
            entry: 'src/routes/give/campaign-card.tsx',
          },
          {
            path: ':organizationSlug/forms/:formSlug',
            lazy: page(() => import('./give/form')),
            entry: 'src/routes/give/form.tsx',
          },
          {
            // Peer-to-peer: a supporter starts their own page for a campaign.
            path: ':organizationSlug/campaigns/:campaignSlug/fundraise',
            lazy: page(() => import('./give/start-fundraiser')),
            entry: 'src/routes/give/start-fundraiser.tsx',
          },
          {
            path: ':organizationSlug/fundraisers/:fundraiserSlug',
            lazy: page(() => import('./give/fundraiser')),
            entry: 'src/routes/give/fundraiser.tsx',
          },
        ],
      },
      // Live Giving public surfaces: no login, no chrome. Bearer-token links.
      { path: 'live/join/:code', lazy: page(() => import('./live/join')), entry: 'src/routes/live/join.tsx' },
      { path: 'live/console/:code', lazy: page(() => import('./live/console')), entry: 'src/routes/live/console.tsx' },
      { path: 'live/board/:token', lazy: page(() => import('./live/board')), entry: 'src/routes/live/board.tsx' },
      { path: 'live/:token', lazy: page(() => import('./live/display')), entry: 'src/routes/live/display.tsx' },
      { path: 'g/:code', lazy: page(() => import('./live/donor')), entry: 'src/routes/live/donor.tsx' },
      { path: 'p/:token', lazy: page(() => import('./live/pledge-pay')), entry: 'src/routes/live/pledge-pay.tsx' },
      // Free tools inside other websites' iframes (no chrome, transparent).
      {
        path: 'embed/tools/:slug',
        lazy: page(() => import('./embed-tool')),
        entry: 'src/routes/embed-tool.tsx',
      },
      // Public utility pages with no chrome
      {
        path: 'email-preferences',
        lazy: page(() => import('./email-preferences')),
        entry: 'src/routes/email-preferences.tsx',
      },
      {
        path: 'manage-donation',
        lazy: page(() => import('./manage-donation')),
        entry: 'src/routes/manage-donation.tsx',
      },
      {
        // A fundraiser's private dashboard, reached from the emailed link.
        path: 'my-fundraiser',
        lazy: page(() => import('./my-fundraiser')),
        entry: 'src/routes/my-fundraiser.tsx',
      },
      // Stripe App connect/link landing — reachable signed-out (the installer
      // arrives straight from Stripe), so it lives outside the dashboard guard
      // and manages its own auth states.
      {
        path: 'stripe-app/connect',
        lazy: page(() => import('./app/stripe-app-connect')),
        entry: 'src/routes/app/stripe-app-connect.tsx',
      },
      // WordPress connect handshake (plain WordPress instructions without params). Reachable
      // signed-out (the site admin arrives from their WP dashboard) and manages
      // its own auth states, so it lives outside the dashboard guard too.
      {
        path: 'wordpress',
        lazy: page(() => import('./wordpress-connect')),
        entry: 'src/routes/wordpress-connect.tsx',
      },
      // 404: explicit prerendered route + SPA catch-all
      { path: '404', element: <NotFoundPage /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]
